import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    CheckCircle,
    XCircle,
    AlertCircle,
    Info,
    X,
    Bell
} from "lucide-react";

const ToastNotification = ({
                               id,
                               title,
                               message,
                               type = "info",
                               duration = 5000,
                               onRemove
                           }) => {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => onRemove(id), 300);
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, id, onRemove]);

    const getTypeStyles = () => {
        switch (type) {
            case "success":
                return {
                    icon: <CheckCircle className="w-5 h-5" />,
                    bg: "bg-green-50 border-green-200",
                    text: "text-green-800",
                    iconColor: "text-green-600"
                };
            case "error":
                return {
                    icon: <XCircle className="w-5 h-5" />,
                    bg: "bg-red-50 border-red-200",
                    text: "text-red-800",
                    iconColor: "text-red-600"
                };
            case "warning":
                return {
                    icon: <AlertCircle className="w-5 h-5" />,
                    bg: "bg-yellow-50 border-yellow-200",
                    text: "text-yellow-800",
                    iconColor: "text-yellow-600"
                };
            default:
                return {
                    icon: <Info className="w-5 h-5" />,
                    bg: "bg-blue-50 border-blue-200",
                    text: "text-blue-800",
                    iconColor: "text-blue-600"
                };
        }
    };

    const typeStyles = getTypeStyles();

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => onRemove(id), 300);
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 100 }}
                    className={`${typeStyles.bg} ${typeStyles.text} border rounded-lg shadow-lg p-4 mb-2 max-w-sm`}
                >
                    <div className="flex items-start">
                        <div className={`flex-shrink-0 ${typeStyles.iconColor}`}>
                            {typeStyles.icon}
                        </div>
                        <div className="ml-3 flex-1">
                            {title && (
                                <p className="font-semibold">{title}</p>
                            )}
                            <p className="text-sm mt-1">{message}</p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Toast Container Component
export const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div className="fixed top-4 right-4 z-50 flex flex-col items-end">
            <AnimatePresence>
                {toasts.map((toast) => (
                    <ToastNotification
                        key={toast.id}
                        {...toast}
                        onRemove={removeToast}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};

// Hook to use toast notifications
export const useToast = () => {
    const [toasts, setToasts] = useState([]);

    const addToast = (toast) => {
        const id = Date.now().toString();
        setToasts((prev) => [...prev, { id, ...toast }]);
        return id;
    };

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    const success = (message, title = "Success") => {
        return addToast({ title, message, type: "success" });
    };

    const error = (message, title = "Error") => {
        return addToast({ title, message, type: "error" });
    };

    const warning = (message, title = "Warning") => {
        return addToast({ title, message, type: "warning" });
    };

    const info = (message, title = "Info") => {
        return addToast({ title, message, type: "info" });
    };

    return {
        toasts,
        addToast,
        removeToast,
        success,
        error,
        warning,
        info,
        ToastContainer: () => <ToastContainer toasts={toasts} removeToast={removeToast} />
    };
};